/**
 * Trefle.io API service for fetching plant characteristics.
 * Routes through the Lambda proxy to avoid CORS issues.
 */

import { sharedDb } from '@monorepo/shared';

/**
 * Get the API endpoint from sync config.
 */
async function getApiEndpoint() {
  const config = await sharedDb.syncConfig.get('sync-config');
  return config?.apiEndpoint || null;
}

/**
 * Search for a species by scientific name via the Lambda proxy.
 */
export async function lookupSpecies(scientificName, token) {
  if (!scientificName || !token) return null;

  const apiEndpoint = await getApiEndpoint();
  if (!apiEndpoint) return null;

  try {
    // Search via proxy
    const searchUrl = `${apiEndpoint}/trefle?path=species/search&token=${encodeURIComponent(token)}&q=${encodeURIComponent(scientificName)}`;
    const searchRes = await fetch(searchUrl);
    if (!searchRes.ok) return null;

    const searchData = await searchRes.json();
    if (!searchData.data || searchData.data.length === 0) return null;

    // Get details via proxy
    const speciesId = searchData.data[0].id;
    const detailUrl = `${apiEndpoint}/trefle?path=species/${speciesId}&token=${encodeURIComponent(token)}`;
    const detailRes = await fetch(detailUrl);
    if (!detailRes.ok) return null;

    const detailData = await detailRes.json();
    const species = detailData.data;

    if (!species) return null;

    return {
      scientificName: species.scientific_name,
      commonName: species.common_name,
      light: mapLight(species.growth?.light),
      moisture: mapMoisture(species.growth?.atmospheric_humidity, species.growth?.minimum_precipitation),
      soil: mapSoil(species.growth?.soil_texture, species.growth?.soil_nutriments),
      bloomMonths: species.growth?.bloom_months || [],
      flowerColor: species.flower?.color ? species.flower.color.join(', ') : '',
      rootDepth: mapRootDepth(species.growth?.root_depth),
      spreadRate: mapSpreadRate(species.growth?.spread),
      droughtTolerance: mapDroughtTolerance(species.growth?.drought_tolerance),
      // Raw values for provenance tracking
      _raw: {
        light: species.growth?.light,
        atmospheric_humidity: species.growth?.atmospheric_humidity,
        minimum_precipitation: species.growth?.minimum_precipitation,
        soil_texture: species.growth?.soil_texture,
        soil_nutriments: species.growth?.soil_nutriments,
        root_depth: species.growth?.root_depth,
        spread: species.growth?.spread,
        drought_tolerance: species.growth?.drought_tolerance,
      },
    };
  } catch (err) {
    console.warn(`Trefle lookup failed for "${scientificName}":`, err.message);
    return null;
  }
}

/**
 * Map Trefle light value (0-10) to descriptive string.
 */
function mapLight(lightValue) {
  if (lightValue === null || lightValue === undefined) return '';
  if (lightValue <= 3) return 'Full shade';
  if (lightValue <= 5) return 'Part shade to shade';
  if (lightValue <= 7) return 'Full sun to part shade';
  return 'Full sun';
}

/**
 * Map Trefle humidity/precipitation to moisture description.
 */
function mapMoisture(humidity, precipitation) {
  if (humidity === null && precipitation === null) return '';
  if (humidity !== null && humidity !== undefined) {
    if (humidity <= 3) return 'Dry';
    if (humidity <= 6) return 'Dry to Moist';
    if (humidity <= 8) return 'Moist to Wet';
    return 'Wet';
  }
  if (precipitation !== null && precipitation !== undefined) {
    if (precipitation < 500) return 'Dry';
    if (precipitation < 1000) return 'Dry to Moist';
    if (precipitation < 1500) return 'Moist';
    return 'Moist to Wet';
  }
  return '';
}

/**
 * Map Trefle soil texture/nutriments to description.
 */
function mapSoil(texture, nutriments) {
  const parts = [];
  if (texture) parts.push(texture === 1 ? 'Sandy' : texture <= 5 ? 'Loam' : texture <= 8 ? 'Clay' : 'Heavy clay');
  if (nutriments) parts.push(nutriments <= 3 ? 'Poor' : nutriments <= 6 ? 'Average' : 'Rich');
  return parts.join(', ') || '';
}

/**
 * Map Trefle root_depth (mm) to shallow/medium/deep.
 */
function mapRootDepth(depthMm) {
  if (depthMm === null || depthMm === undefined) return '';
  if (depthMm < 300) return 'shallow';    // <12"
  if (depthMm < 900) return 'medium';     // 12-36"
  return 'deep';                           // >36"
}

/**
 * Map Trefle spread (cm) to slow/moderate/aggressive.
 * Trefle's 'spread' is mature canopy/clump diameter, not colonization rate.
 * Most perennials are 30-90cm, shrubs 100-300cm. 
 * We can't reliably infer colonization rate from this, so skip it.
 */
function mapSpreadRate(spreadCm) {
  // Trefle's spread field is mature plant diameter, not colonization rate.
  // Can't reliably map this to slow/moderate/aggressive spread behavior.
  return '';
}

/**
 * Map Trefle drought_tolerance (0-10) to none/moderate/high.
 */
function mapDroughtTolerance(droughtVal) {
  if (droughtVal === null || droughtVal === undefined) return '';
  if (droughtVal <= 3) return 'none';
  if (droughtVal <= 6) return 'moderate';
  return 'high';
}

/**
 * Derive flood tolerance from NWPL wetland indicator rating.
 */
export function deriveFloodToleranceFromNWPL(agcpRating, empRating) {
  // Use the "wetter" of the two ratings
  const rating = (agcpRating || empRating || '').toUpperCase().trim();
  if (rating === 'OBL') return 'permanent';
  if (rating === 'FACW') return 'extended';
  if (rating === 'FAC' || rating === 'FAC+') return 'moderate';
  if (rating === 'FACU' || rating === 'FACU+' || rating === 'FACU-') return 'brief';
  if (rating === 'UPL') return 'none';
  return '';
}

/**
 * Map bloom months array to season string.
 */
function bloomMonthsToSeason(months) {
  if (!months || months.length === 0) return '';
  const seasonMap = {
    jan: 'Winter', feb: 'Winter', mar: 'Spring',
    apr: 'Spring', may: 'Spring', jun: 'Summer',
    jul: 'Summer', aug: 'Summer', sep: 'Fall',
    oct: 'Fall', nov: 'Fall', dec: 'Winter',
  };
  const seasons = [...new Set(months.map(m => seasonMap[m.toLowerCase()] || ''))].filter(Boolean);
  return seasons.join(' to ');
}

/**
 * Backfill species data from Trefle for all species missing characteristics.
 * Returns { updated: number, failed: number, skipped: number }
 */
export async function backfillFromTrefle(speciesDb, recordChange, token, appDb) {
  if (!token) return { updated: 0, failed: 0, skipped: 0, error: 'Trefle API token is required' };

  const allSpecies = await speciesDb.plantSpecies.toArray();
  let updated = 0;
  let failed = 0;
  let skipped = 0;

  for (const species of allSpecies) {
    // Skip if already has all key data filled
    if (species.lightRequirements && species.moistureRequirements && species.soilRequirements && species.bloomSeason && species.bloomColor) {
      skipped++;
      continue;
    }

    // Rate limit: wait 500ms between requests (Trefle free tier)
    await new Promise(resolve => setTimeout(resolve, 500));

    const data = await lookupSpecies(species.scientificName, token);
    if (!data) {
      failed++;
      continue;
    }

    const updates = {};
    if (!species.lightRequirements && data.light) updates.lightRequirements = data.light;
    if (!species.moistureRequirements && data.moisture) updates.moistureRequirements = data.moisture;
    if (!species.soilRequirements && data.soil) updates.soilRequirements = data.soil;
    if (!species.bloomSeason && data.bloomMonths.length > 0) updates.bloomSeason = bloomMonthsToSeason(data.bloomMonths);
    if (!species.bloomColor && data.flowerColor) updates.bloomColor = data.flowerColor;

    if (Object.keys(updates).length > 0) {
      const merged = { ...species, ...updates, updatedAt: new Date().toISOString() };
      await speciesDb.plantSpecies.put(merged);
      if (recordChange) recordChange('plantSpecies', species.id, 'put');

      // Propagate to all plant instances of this species (in app-specific DB)
      if (appDb && appDb.plants) {
        const instances = await appDb.plants.toArray();
        for (const inst of instances) {
          if (inst.scientificName === species.scientificName && inst.commonName === species.commonName) {
            const instUpdates = {};
            for (const [key, val] of Object.entries(updates)) {
              if (!inst[key]) instUpdates[key] = val;
            }
            if (Object.keys(instUpdates).length > 0) {
              await appDb.plants.put({ ...inst, ...instUpdates, updatedAt: new Date().toISOString() });
              if (recordChange) recordChange('plants', inst.id, 'put');
            }
          }
        }
      }

      updated++;
    } else {
      skipped++;
    }
  }

  return { updated, failed, skipped };
}
